import { c as _c } from "react-compiler-runtime";
import { createContext, useContext, useMemo } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, Label, useFieldIds } from "@/components/form";
import { Text } from "@/components/primitives";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/core/utils";
type CheckboxOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};
type CheckboxGroupFieldContextValue = {
  groupId: string;
  labelId: string;
  hintId: string;
  errorId: string;
  firstItemId: string | null;
};
const CheckboxGroupFieldContext = createContext<CheckboxGroupFieldContextValue | null>(null);
function useCheckboxGroupFieldContext() {
  const ctx = useContext(CheckboxGroupFieldContext);
  if (!ctx) {
    throw new globalThis.Error("CheckboxGroupField components must be used within CheckboxGroupField.Root");
  }
  return ctx;
}
function getOptionId(groupId: string, value: string) {
  const safe = value.trim().replaceAll(/\s+/g, "-");
  return `${groupId}-option-${safe || "empty"}`;
}
type CheckboxGroupLabelProps = {
  text: string;
  invalid?: boolean;
};
function CheckboxGroupLabel(t0) {
  const $ = _c(5);
  const {
    text,
    invalid
  } = t0;
  const {
    firstItemId,
    labelId
  } = useCheckboxGroupFieldContext();
  const t1 = firstItemId ?? labelId;
  let t2;
  if ($[0] !== invalid || $[1] !== labelId || $[2] !== t1 || $[3] !== text) {
    t2 = <Label htmlFor={t1} id={labelId} invalid={invalid} text={text} />;
    $[0] = invalid;
    $[1] = labelId;
    $[2] = t1;
    $[3] = text;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type CheckboxGroupHintProps = {
  text: string;
};
function CheckboxGroupHint(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  const {
    hintId
  } = useCheckboxGroupFieldContext();
  let t1;
  if ($[0] !== hintId || $[1] !== text) {
    t1 = <HintMessage id={hintId} text={text} />;
    $[0] = hintId;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type CheckboxGroupErrorMessageProps = {
  text: string;
};
function CheckboxGroupErrorMessage(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    groupId,
    errorId
  } = useCheckboxGroupFieldContext();
  let t1;
  if ($[0] !== errorId || $[1] !== groupId || $[2] !== text) {
    t1 = <ErrorMessage htmlFor={groupId} id={errorId} text={text} />;
    $[0] = errorId;
    $[1] = groupId;
    $[2] = text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type RootProps = {
  children: React.ReactNode;
  name: string;
  options: readonly CheckboxOption[];
};
function Root(t0) {
  const $ = _c(15);
  const {
    children,
    name,
    options
  } = t0;
  const fieldIds = useFieldIds(name);
  let t1;
  if ($[0] !== fieldIds.inputId || $[1] !== options[0] || $[2] !== options.length) {
    t1 = options.length > 0 ? getOptionId(fieldIds.inputId, options[0].value) : null;
    $[0] = fieldIds.inputId;
    $[1] = options[0];
    $[2] = options.length;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const firstItemId = t1;
  let t2;
  if ($[4] !== fieldIds.errorId || $[5] !== fieldIds.hintId || $[6] !== fieldIds.inputId || $[7] !== fieldIds.labelId || $[8] !== firstItemId) {
    t2 = {
      groupId: fieldIds.inputId,
      labelId: fieldIds.labelId,
      hintId: fieldIds.hintId,
      errorId: fieldIds.errorId,
      firstItemId
    };
    $[4] = fieldIds.errorId;
    $[5] = fieldIds.hintId;
    $[6] = fieldIds.inputId;
    $[7] = fieldIds.labelId;
    $[8] = firstItemId;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const value = t2;
  let t3;
  if ($[10] !== children) {
    t3 = <div className="flex flex-col gap-1 text-left">{children}</div>;
    $[10] = children;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3 || $[13] !== value) {
    t4 = <CheckboxGroupFieldContext.Provider value={value}>{t3}</CheckboxGroupFieldContext.Provider>;
    $[12] = t3;
    $[13] = value;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
type GroupProps = {
  options: readonly CheckboxOption[];
  value: readonly string[];
  onValueChange: (next: string[]) => void;
  disabled?: boolean;
  className?: string;
  invalid?: boolean;
  /** Marks the whole group as a required field for assistive tech. */
  required?: boolean;
  onBlur?: () => void;
};
function Group(t0) {
  const $ = _c(27);
  const {
    options,
    value,
    onValueChange,
    disabled,
    className,
    invalid,
    required,
    onBlur
  } = t0;
  const {
    errorId,
    groupId,
    hintId,
    labelId
  } = useCheckboxGroupFieldContext();
  let t1;
  if ($[0] !== onValueChange || $[1] !== value) {
    t1 = (optValue, checked) => {
      const current = new Set(value);
      if (checked) {
        current.add(optValue);
      } else {
        current.delete(optValue);
      }
      onValueChange(Array.from(current));
    };
    $[0] = onValueChange;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setChecked = t1;
  const t2 = `${hintId} ${errorId}`;
  const t3 = required || undefined;
  let t4;
  if ($[3] !== className) {
    t4 = cn("mt-2 grid gap-2", className);
    $[3] = className;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== disabled || $[6] !== groupId || $[7] !== invalid || $[8] !== onBlur || $[9] !== options || $[10] !== setChecked || $[11] !== value) {
    let t6;
    if ($[13] !== disabled || $[14] !== groupId || $[15] !== invalid || $[16] !== onBlur || $[17] !== setChecked || $[18] !== value) {
      t6 = opt => {
        const id = getOptionId(groupId, opt.value);
        const isChecked = value.includes(opt.value);
        const isDisabled = !!disabled || !!opt.disabled;
        return <div className="flex items-start gap-2" key={opt.value}><Checkbox aria-invalid={invalid || undefined} checked={isChecked} className={cn(invalid && "border-red-600 data-[state=checked]:bg-red-600")} disabled={isDisabled} id={id} onBlur={onBlur} onCheckedChange={checked_0 => {
            setChecked(opt.value, checked_0 === true);
          }} /><label className={cn("grid gap-0.5", isDisabled && "cursor-not-allowed opacity-50")} htmlFor={id}><Text component="span" text={opt.label} tone="foreground" variant="body-sm" weight="medium" />{opt.description && <Text component="span" text={opt.description} tone="muted" variant="caption" />}</label></div>;
      };
      $[13] = disabled;
      $[14] = groupId;
      $[15] = invalid;
      $[16] = onBlur;
      $[17] = setChecked;
      $[18] = value;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    t5 = options.map(t6);
    $[5] = disabled;
    $[6] = groupId;
    $[7] = invalid;
    $[8] = onBlur;
    $[9] = options;
    $[10] = setChecked;
    $[11] = value;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[20] !== groupId || $[21] !== labelId || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t6 = <div aria-describedby={t2} aria-labelledby={labelId} aria-required={t3} className={t4} id={groupId} role="group">{t5}</div>;
    $[20] = groupId;
    $[21] = labelId;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}
type NativeProps = {
  name: string;
  label: string;
  hint?: string;
  options: readonly CheckboxOption[];
  value: readonly string[];
  onValueChange: (next: string[]) => void;
  disabled?: boolean;
  className?: string;
};
function Native(t0) {
  const $ = _c(20);
  const {
    name,
    label,
    hint,
    options,
    value,
    onValueChange,
    disabled,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <CheckboxGroupLabel text={label} />;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== onValueChange || $[6] !== options || $[7] !== value) {
    t3 = <Group className="mt-2" disabled={disabled} options={options} value={value} onValueChange={onValueChange} />;
    $[4] = disabled;
    $[5] = onValueChange;
    $[6] = options;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== hint) {
    t4 = hint && <CheckboxGroupHint text={hint} />;
    $[9] = hint;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <div className={t1}>{t2}{t3}{t4}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== name || $[17] !== options || $[18] !== t5) {
    t6 = <Root name={name} options={options}>{t5}</Root>;
    $[16] = name;
    $[17] = options;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
type RHF<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  hint?: string;
  options: readonly CheckboxOption[];
  disabled?: boolean;
  className?: string;
  /** Marks the group as a required field for assistive tech; enforcement stays in the form schema. */
  required?: boolean;
};
function RHF(t0) {
  const $ = _c(33);
  const {
    name,
    label,
    hint,
    options,
    disabled,
    className,
    required
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = {
      name
    };
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    field,
    fieldState
  } = useController(t1);
  const errorText = fieldState.error?.message;
  let t2;
  if ($[2] !== field.value) {
    t2 = Array.isArray(field.value) ? field.value as string[] : [];
    $[2] = field.value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const value = t2;
  let t3;
  if ($[4] !== className) {
    t3 = cn(className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!errorText;
  let t5;
  if ($[6] !== label || $[7] !== t4) {
    t5 = <CheckboxGroupLabel invalid={t4} text={label} />;
    $[6] = label;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = !!errorText;
  let t7;
  if ($[9] !== disabled || $[10] !== field.onBlur || $[11] !== field.onChange || $[12] !== options || $[13] !== required || $[14] !== t6 || $[15] !== value) {
    t7 = <Group disabled={disabled} invalid={t6} options={options} required={required} value={value} onBlur={field.onBlur} onValueChange={field.onChange} />;
    $[9] = disabled;
    $[10] = field.onBlur;
    $[11] = field.onChange;
    $[12] = options;
    $[13] = required;
    $[14] = t6;
    $[15] = value;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== hint) {
    t8 = hint && <CheckboxGroupHint text={hint} />;
    $[17] = hint;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== errorText) {
    t9 = errorText && <CheckboxGroupErrorMessage text={errorText} />;
    $[19] = errorText;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <div className="h-2">{t9}</div>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t3 || $[25] !== t5 || $[26] !== t7 || $[27] !== t8) {
    t11 = <div className={t3}>{t5}{t7}{t8}{t10}</div>;
    $[23] = t10;
    $[24] = t3;
    $[25] = t5;
    $[26] = t7;
    $[27] = t8;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== name || $[30] !== options || $[31] !== t11) {
    t12 = <Root name={name} options={options}>{t11}</Root>;
    $[29] = name;
    $[30] = options;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}
export const CheckboxGroupField = {
  RHF,
  Native
};